import { useEffect, useState, memo, ReactElement } from 'react';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';

import StyledWrapper from './style';
import { OverridableStringUnion } from '@mui/types';
import { AlertColor, AlertPropsColorOverrides } from '@mui/material/Alert/Alert';
import { MESSAGE_PROGRAM_CACHED_TABLE_TAKE_TIME_TO_SYNC } from '../../pages/ProgramSetup/subPages/StagesAndForms/constants';

interface Alert {
	type: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
	message?: ReactElement | string;
	isClosable?: boolean;
}

const createAlertMessage = (
	type: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>,
	message: ReactElement | string,
	isClosable?: boolean
): Alert => ({
	type,
	message,
	isClosable: isClosable != undefined ? isClosable : type !== 'warning'
});

interface AppProcess {
	[key: string]: unknown;
	type: string;
	appId: string;
}

interface Props {
	data: AppProcess[];
	isReadyToPublish: boolean;
	allowInfoBalloonClose?: boolean;
	assigned?: boolean;
}

const AppProcessWarningsMemo = ({
	data,
	isReadyToPublish,
	allowInfoBalloonClose,
	assigned = false
}: Props) => {
	const [alerts, setAlerts] = useState<Alert[]>([]);

	const [appProcessExists, setAppProcessExists] = useState(false);
	const [submissionStageExists, setSubmissionStageExists] = useState(false);
	const [formExists, setFormExists] = useState(false);
	const [allStagesHaveForms, setAllStagesHaveForms] = useState(true);
	const [lastStageType, setLastStageType] = useState<string | null>(null);

	useEffect(() => {
		data?.map((e) => {
			if (e?.type === 'APPLICATION PROCESS' && e?.appId) {
				setAppProcessExists(true);
			} else if (e?.type === 'Submission Stage') {
				setSubmissionStageExists(true);
			} else if (e?.type === 'Application Form' || e?.type === 'Review Form') {
				setFormExists(true);
			}

			if (e?.hasForms === false) {
				setAllStagesHaveForms(false);
			}

			if (e?.type?.includes('Stage')) {
				setLastStageType(e?.type);
			}
		});
	}, [data]);

	useEffect(() => {
		if (isReadyToPublish) {
			setAlerts([
				createAlertMessage(
					'info',
					<StyledWrapper.CustomText data-testid={'warning-title'}>
						The Application was successfully setup and ready to publish.
					</StyledWrapper.CustomText>
				)
			]);
		} else if (assigned) {
			setAlerts([
				createAlertMessage(
					'info',
					<StyledWrapper.CustomText data-testid={'warning-title'}>
						If any of them are already assigned to any of the selected stages, they will remain in
						place.
						<br />
						Select stages from below
					</StyledWrapper.CustomText>
				)
			]);
		} else if (lastStageType === 'Submission Stage') {
			setAlerts([
				createAlertMessage(
					'info',
					<StyledWrapper.CustomText warningText data-testid={'warning-footer'}>
						Stage Blocks need to be closed with an Evaluation Stage in order to publish the
						Application Process.
					</StyledWrapper.CustomText>
				)
			]);
		} else if (!allStagesHaveForms) {
			setAlerts([
				createAlertMessage(
					'info',
					<StyledWrapper.CustomText warningText data-testid={'warning-footer'}>
						All Stages require at least 1 form.
					</StyledWrapper.CustomText>
				)
			]);
		} else if (formExists) {
			setAlerts([
				createAlertMessage(
					'info',
					<>
						<StyledWrapper.CustomText data-testid={'warning-title'} bold>
							The minimum requirements to publish your solicitation have been achieved.{' '}
						</StyledWrapper.CustomText>
						<ul>
							<li>
								You may continue to add stages and forms to your application. When you are done,
								click the 'Ready to Publish' button to finalize your application.
							</li>
							<li>
								Once you publish, you will no longer be able to add new stages to your solicitation
								or mark fields as required.
							</li>
						</ul>
						<StyledWrapper.CustomText warningText data-testid={'warning-footer'}>
							Therefore verify twice if everything is setup correctly before you publish the
							settings.
						</StyledWrapper.CustomText>
					</>
				)
			]);
		} else if (submissionStageExists) {
			setAlerts([
				createAlertMessage(
					'warning',
					<StyledWrapper.CustomText warningText>
						The Application Process is not setup.
						<br />
						Add at least a form to the stage
					</StyledWrapper.CustomText>
				)
			]);
		} else if (!appProcessExists) {
			setAlerts([
				createAlertMessage(
					'warning',
					<StyledWrapper.CustomText warningText>
						The Application Process is not setup.
						<br />
						You can use one of the options listed below.
						<br />
					</StyledWrapper.CustomText>
				),
				createAlertMessage(
					'info',
					<StyledWrapper.CustomText>
						{MESSAGE_PROGRAM_CACHED_TABLE_TAKE_TIME_TO_SYNC}
					</StyledWrapper.CustomText>,
					false
				)
			]);
		} else {
			setAlerts([
				createAlertMessage(
					'warning',
					<StyledWrapper.CustomText warningText>
						Your Application is not yet ready to publish.
						<br />
						Your Application Process must include at least one Submission Stage (with at least one
						form) and one Evaluation Stage (with at least one form) before it can be marked for
						publication.
					</StyledWrapper.CustomText>
				)
			]);
		}
	}, [appProcessExists, submissionStageExists, formExists, allStagesHaveForms, lastStageType]);

	return (
		<>
			{alerts.map((alert, alertIndex) => (
				<Collapse key={`${alert.type}-${alertIndex}`} in={!!alert.type}>
					<Stack sx={{ width: '100%', marginBottom: '20px' }} spacing={2}>
						<Alert
							action={
								allowInfoBalloonClose &&
								alert.isClosable && (
									<IconButton
										data-testid={'warning-close-button'}
										aria-label="close"
										color="inherit"
										size="small"
										onClick={() => {
											setAlerts((prevAlerts) =>
												prevAlerts.filter((_, indexToRemove) => alertIndex !== indexToRemove)
											);
										}}
									>
										<CloseIcon fontSize="inherit" />
									</IconButton>
								)
							}
							variant="outlined"
							severity={alert.type}
						>
							{alert.message}
						</Alert>
					</Stack>
				</Collapse>
			))}
		</>
	);
};

const AppProcessWarnings = memo(AppProcessWarningsMemo);

export default AppProcessWarnings;
